/**
 * Platform services. The same React UI runs in a browser tab and inside the desktop app; the
 * desktop app exposes a narrow, typed bridge on `window.deskworksDesktop` (see apps/desktop
 * preload). Browser capabilities are detected individually; an unavailable capability never
 * hides the rest of the product.
 */

export type DesktopUpdateState =
  | { status: 'unsupported'; reason: string }
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'up-to-date'; checkedAt: string }
  | { status: 'downloading'; version: string; percent: number }
  | { status: 'ready'; version: string }
  | { status: 'error'; message: string };

export interface DesktopBridge {
  version: string;
  platform: 'win32' | 'darwin' | 'linux';
  notify(title: string, body: string, link: string): void;
  setBadgeCount(count: number): void;
  openExternal(url: string): void;
  onNavigate(handler: (path: string) => void): () => void;
  /** Desktop app 0.1.1 and later. */
  updates?: {
    state(): Promise<DesktopUpdateState | null>;
    check(): Promise<DesktopUpdateState | null>;
    restart(): void;
    onChange(handler: (state: DesktopUpdateState) => void): () => void;
  };
}

declare global {
  interface Window {
    deskworksDesktop?: DesktopBridge;
  }
}

export const desktop: DesktopBridge | null = typeof window !== 'undefined' ? (window.deskworksDesktop ?? null) : null;

export const capabilities = {
  notifications: typeof window !== 'undefined' && 'Notification' in window,
  media: typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia,
  screenShare: typeof navigator !== 'undefined' && !!navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices,
  indexedDb: typeof indexedDB !== 'undefined',
  broadcast: typeof BroadcastChannel !== 'undefined',
  secureContext: typeof window !== 'undefined' && window.isSecureContext,
};

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (desktop) return 'granted';
  return capabilities.notifications ? Notification.permission : 'unsupported';
}

export async function requestNotificationPermission() {
  if (desktop || !capabilities.notifications) return notificationPermission();
  return Notification.requestPermission();
}

/** Shows a system notification when permitted. Bodies are only included when the user allowed previews. */
export function systemNotify(title: string, body: string, link: string, navigate: (path: string) => void) {
  if (desktop) {
    desktop.notify(title, body, link);
    return;
  }
  if (!capabilities.notifications || Notification.permission !== 'granted' || document.visibilityState === 'visible') return;
  const n = new Notification(title, { body, icon: '/icon-192.png', tag: link });
  n.onclick = () => {
    window.focus();
    navigate(link);
    n.close();
  };
}

export function setBadge(count: number) {
  if (desktop) desktop.setBadgeCount(count);
  else if ('setAppBadge' in navigator) {
    const nav = navigator as Navigator & { setAppBadge(n?: number): Promise<void>; clearAppBadge(): Promise<void> };
    (count ? nav.setAppBadge(count) : nav.clearAppBadge()).catch(() => undefined);
  }
  document.title = count ? `(${count}) DeskWorks One` : 'DeskWorks One';
}

export function openExternal(url: string) {
  if (desktop) desktop.openExternal(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

/* ------------------------------------------------------------------ web push */

export const webPushSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && !desktop;

function keyBytes(base64url: string) {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!webPushSupported) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Opt-in only: registers the push service worker and returns the subscription to store on the server. */
export async function subscribeWebPush(publicKey: string): Promise<PushSubscription> {
  if (!webPushSupported) throw new Error('This browser does not support push notifications.');
  if ((await requestNotificationPermission()) !== 'granted') throw new Error('Notifications are blocked for this site.');
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
}

export async function unsubscribeWebPush() {
  const sub = await currentPushSubscription();
  await sub?.unsubscribe();
}
